import { motion } from "framer-motion";
import {
  Layers,
  Zap,
  Sparkles,
  Fingerprint,
  MonitorSmartphone,
  BookOpen,
  Languages,
  Volume2,
  Wand2,
  Search,
  Accessibility,
  BarChart3,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const FEATURE_META = [
  { icon: BookOpen, accent: "#E6007E", span: "md:col-span-8" },
  { icon: MonitorSmartphone, accent: "#00A6E6", span: "md:col-span-4" },
  { icon: Sparkles, accent: "#8BC53F", span: "md:col-span-4" },
  { icon: Layers, accent: "#F7941D", span: "md:col-span-4" },
  { icon: Zap, accent: "#6E2C9A", span: "md:col-span-4" },
];

const AI_ICONS = [Languages, Volume2, Wand2, Search, Accessibility, BarChart3];

const fadeUp = {
  hidden: { y: 24, opacity: 0 },
  show: (i = 0) => ({ y: 0, opacity: 1, transition: { duration: 0.6, delay: 0.05 * i, ease: "easeOut" } }),
};

export default function Features() {
  const { t, lang } = useLanguage();
  return (
    <section id="features" data-testid="features-section" className="relative py-32 md:py-40">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 mb-16 items-end">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="md:col-span-8"
          >
            <p className="text-xs tracking-[0.22em] uppercase text-[#E6007E] mb-4">{t.features.kicker}</p>
            <h2 className="font-display text-4xl md:text-6xl font-light tracking-tighter text-balance">
              {lang === "ar" ? (
                <>كشك صحف <span className="italic">ينبض بالحياة</span>.</>
              ) : (
                <>A newsstand that <span className="italic">breathes</span>.</>
              )}
            </h2>
          </motion.div>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="md:col-span-4 text-zinc-400 leading-relaxed"
          >
            {t.features.sub}
          </motion.p>
        </div>

        <div className="grid md:grid-cols-12 gap-6">
          {t.features.cards.map((f, i) => {
            const meta = FEATURE_META[i];
            const Icon = meta.icon;
            return (
              <motion.article
                key={f.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                custom={i}
                viewport={{ once: true, margin: "-60px" }}
                data-testid={`feature-card-${i}`}
                className={`${meta.span} group relative overflow-hidden rounded-3xl border border-white/10 bg-zinc-900/40 p-8 md:p-10 hover:-translate-y-1 transition-transform duration-300`}
              >
                <div
                  className="absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl opacity-30 transition-opacity duration-500 group-hover:opacity-60"
                  style={{ backgroundColor: meta.accent }}
                />
                <div className="relative">
                  <div
                    className="inline-flex items-center justify-center h-12 w-12 rounded-2xl glass mb-6"
                    style={{ color: meta.accent }}
                  >
                    <Icon size={22} />
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-3">{f.title}</h3>
                  <p className="text-zinc-400 leading-relaxed max-w-xl">{f.body}</p>
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* Publisher-Grade AI feature block */}
        <motion.article
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          data-testid="feature-publisher-grade"
          className="mt-6 relative overflow-hidden rounded-3xl border border-white/10 bg-zinc-900/40 p-8 md:p-14"
        >
          <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#00A6E6]/20 blur-[140px] pointer-events-none" />
          <div className="absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full bg-[#E6007E]/15 blur-[140px] pointer-events-none" />

          <div className="relative grid md:grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14 items-end">
            <div className="md:col-span-8">
              <div
                className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 mb-6"
                style={{ color: "#00A6E6" }}
              >
                <Fingerprint size={14} />
                <span className="text-xs tracking-[0.18em] uppercase text-zinc-200">
                  {t.features.aiBadge}
                </span>
              </div>
              <h3 className="font-display text-3xl md:text-5xl font-light tracking-tighter text-balance">
                {lang === "ar" ? (
                  <>مجلة <span className="italic">تفكّر وتتحدث وتسافر</span>.</>
                ) : (
                  <>A magazine that <span className="italic">thinks, speaks &amp; travels</span>.</>
                )}
              </h3>
            </div>
            <p className="md:col-span-4 text-zinc-400 leading-relaxed">
              {t.features.aiSub}
            </p>
          </div>

          <div className="relative grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.features.aiCards.map((f, i) => {
              const Icon = AI_ICONS[i];
              return (
                <motion.div
                  key={f.title}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="show"
                  custom={i}
                  viewport={{ once: true, margin: "-40px" }}
                  data-testid={`ai-feature-${i}`}
                  className="rounded-2xl border border-white/10 bg-zinc-950/40 p-6"
                >
                  <div
                    className="inline-flex items-center justify-center h-10 w-10 rounded-xl glass mb-4"
                    style={{ color: "#00A6E6" }}
                  >
                    <Icon size={18} />
                  </div>
                  <h4 className="font-display text-xl tracking-tight mb-2 text-zinc-100">
                    {f.title}
                  </h4>
                  <p className="text-sm text-zinc-400 leading-relaxed">{f.body}</p>
                </motion.div>
              );
            })}
          </div>

          <p className="relative mt-10 text-xs text-zinc-500 tracking-[0.22em] uppercase">
            {t.features.aiFooter}
          </p>
        </motion.article>
      </div>
    </section>
  );
}
